import { useState, useMemo } from 'react';
import { Filter } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTags } from '@/hooks/use-tags';
import type { Tag, TagKind } from '@/types/tags';
import { TagBadge } from './tag-badge';

interface TagFilterPickerProps {
  selectedIds: string[];
  onToggle: (tagId: string) => void;
  onClear: () => void;
}

// Multi-select popover for filtering students by tags across every kind
// (labels, segments, stages, milestones). Groups the option list by kind
// so a teacher can quickly scope by lifecycle, cohort, or label in one
// control. Different from the TagPicker used for label assignment because
// this version:
//   - is kind-agnostic (no `kind` prop — shows everything visible)
//   - surfaces the total-selected badge on the trigger button
//   - groups by kind with visible section headers
//   - filters the in-memory list by the user's search input
export function TagFilterPicker({
  selectedIds,
  onToggle,
  onClear,
}: TagFilterPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Fetch every tag kind in one shot. The dashboard already caches this
  // query under ['tags','list', {}], so this is free when /tags was
  // visited earlier in the session.
  const { data: tags = [], isLoading } = useTags();

  // Group by kind, apply the in-memory search filter.
  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const buckets: Record<TagKind, Tag[]> = {
      label: [],
      segment: [],
      stage: [],
      milestone: [],
    };
    for (const tag of tags) {
      if (q && !tag.name.toLowerCase().includes(q) && !tag.slug.toLowerCase().includes(q)) {
        continue;
      }
      buckets[tag.kind].push(tag);
    }
    // Stages come pre-sorted by stage_order from the backend.
    return buckets;
  }, [tags, search]);

  const selectedSet = new Set(selectedIds);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={(p) => (
          <Button {...p} variant="outline" size="sm">
            <Filter className="mr-2 h-4 w-4" />
            Tags
            {selectedIds.length > 0 && (
              <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                {selectedIds.length}
              </Badge>
            )}
          </Button>
        )}
      />
      <PopoverContent className="w-80 p-0" align="start">
        <div className="flex items-center gap-2 border-b p-2">
          <Input
            placeholder="Search tags…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8"
          />
          {selectedIds.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClear}
              title="Clear all tag filters"
            >
              Clear
            </Button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto py-1">
          {isLoading && (
            <p className="px-3 py-2 text-xs text-muted-foreground">Loading…</p>
          )}
          {!isLoading && tags.length === 0 && (
            <p className="px-3 py-2 text-xs text-muted-foreground">No tags defined.</p>
          )}
          {(['label', 'segment', 'stage', 'milestone'] as TagKind[]).map((kind) => {
            const bucket = grouped[kind];
            if (bucket.length === 0) return null;
            return (
              <div key={kind}>
                <p className="bg-muted/40 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {kindLabel(kind)}
                </p>
                {bucket.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => onToggle(tag.id)}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent"
                  >
                    <Checkbox checked={selectedSet.has(tag.id)} />
                    <TagBadge tag={tag} />
                    {tag.assignment_count != null && (
                      <span className="ml-auto text-xs text-muted-foreground">
                        {tag.assignment_count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function kindLabel(kind: TagKind): string {
  switch (kind) {
    case 'label':
      return 'Labels';
    case 'segment':
      return 'Segments';
    case 'stage':
      return 'Stages';
    case 'milestone':
      return 'Milestones';
  }
}
